import { useEffect, useRef } from "react";
import {
  GOOGLE_OAUTH_CLIENT_ID,
  isGoogleOAuthEnabled,
} from "../google-oauth";

/**
 * Bouton « Continuer avec Google » via Google Identity Services (GIS).
 *
 * Charge `https://accounts.google.com/gsi/client` à la demande puis rend un
 * bouton officiel Google qui renvoie un ID token JWT à `onCredential`.
 *
 * Si `VITE_GOOGLE_OAUTH_CLIENT_ID` n'est pas défini, le composant ne rend rien.
 */

declare global {
  interface Window {
    google?: {
      accounts?: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            ux_mode?: "popup" | "redirect";
          }) => void;
          renderButton: (
            target: HTMLElement,
            options: {
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "small" | "medium" | "large";
              type?: "standard" | "icon";
              width?: number | string;
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill";
            },
          ) => void;
        };
      };
    };
  }
}

const GIS_SCRIPT_ID = "google-identity-services";

const loadGisScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById(GIS_SCRIPT_ID)) return resolve();
    const script = document.createElement("script");
    script.id = GIS_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load GIS"));
    document.head.appendChild(script);
  });

interface Props {
  onCredential: (idToken: string) => void;
  label?: "signin_with" | "signup_with" | "continue_with";
  disabled?: boolean;
}

export const GoogleSignInButton = ({
  onCredential,
  label = "continue_with",
  disabled = false,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const clientId = GOOGLE_OAUTH_CLIENT_ID;

  useEffect(() => {
    if (!isGoogleOAuthEnabled() || !clientId || !containerRef.current) return;
    let cancelled = false;
    void loadGisScript()
      .then(() => {
        if (cancelled) return;
        const gid = window.google?.accounts?.id;
        if (!gid || !containerRef.current) return;
        gid.initialize({
          client_id: clientId,
          callback: (resp) => {
            if (resp?.credential) onCredential(resp.credential);
          },
          ux_mode: "popup",
        });
        gid.renderButton(containerRef.current, {
          theme: "outline",
          size: "large",
          type: "standard",
          shape: "rectangular",
          text: label,
          width: containerRef.current.offsetWidth,
        });
      })
      .catch(() => {
        // Silencieux : l'utilisateur a toujours mot de passe en fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, label, onCredential]);

  if (!isGoogleOAuthEnabled()) return null;
  return (
    <div
      ref={containerRef}
      className={`flex justify-center ${disabled ? "pointer-events-none opacity-50" : ""}`}
      aria-disabled={disabled || undefined}
    />
  );
};
